import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import RangeSlider from '../molecules/RangeSlider'
import SelectDropdown from '../molecules/SelectDropdown'
import Toggle from '../atoms/Toggle'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import CopyButton from '../molecules/CopyButton'
import {
  setGeneratorMode,
  setLength, setIncludeUppercase, setIncludeLowercase, setIncludeNumbers, setIncludeSymbols,
  setExcludeAmbiguous, setMinNumbers, setMinSymbols, setBaseWord, setBulkCount,
  generatePassword, generateMultiplePasswords, clearPasswords,
  setPassphraseWordCount, setPassphraseSeparator, setPassphraseCapitalize, setPassphraseIncludeNumber,
  generatePassphrase,
  setUsernameStyle, setBaseEmail, generateUsername, clearUsernames,
  clearHistory,
} from '../store/slices/passwordSlice'
import './PasswordPage.css'

const SEPARATOR_OPTIONS = [
  { value: '-', label: 'Hyphen  (-)' },
  { value: '_', label: 'Underscore  (_)' },
  { value: '.', label: 'Period  (.)' },
  { value: ' ', label: 'Space' },
]

const USERNAME_STYLE_OPTIONS = [
  { value: 'word-number', label: 'Random word + number' },
  { value: 'plus-address', label: 'Email plus-addressing (yours+tag@domain)' },
]

const PasswordPage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const {
    generatorMode,
    length, includeUppercase, includeLowercase, includeNumbers, includeSymbols, excludeAmbiguous,
    minNumbers, minSymbols, baseWord, password, passwords, strength, entropyBits, crackTime, bulkCount,
    passphraseWordCount, passphraseSeparator, passphraseCapitalize, passphraseIncludeNumber,
    passphrase, passphraseEntropyBits, passphraseCrackTime,
    usernameStyle, baseEmail, username, usernames,
    history,
  } = useSelector(state => state.password)
  const [lastUpdated, setLastUpdated] = useState(null)

  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  useEffect(() => {
    dispatch(generatePassword())
  }, [length, includeUppercase, includeLowercase, includeNumbers, includeSymbols, excludeAmbiguous, minNumbers, minSymbols, baseWord, dispatch])

  const handleGenerate = () => {
    dispatch(generatePassword())
  }

  const handleGenerateMultiple = () => {
    dispatch(generateMultiplePasswords(bulkCount))
  }

  const getStrengthColor = (strength) => {
    switch (strength) {
      case 'weak': return 'var(--danger)'
      case 'fair': return 'var(--warning)'
      case 'strong': return 'var(--accent)'
      case 'very-strong': return '#00d484'
      default: return 'var(--text-muted)'
    }
  }

  const getStrengthText = (strength) => {
    switch (strength) {
      case 'weak': return 'Weak'
      case 'fair': return 'Fair'
      case 'strong': return 'Strong'
      case 'very-strong': return 'Very Strong'
      default: return 'Unknown'
    }
  }

  const getStrengthWidth = (strength) => {
    switch (strength) {
      case 'weak': return '25%'
      case 'fair': return '50%'
      case 'strong': return '75%'
      case 'very-strong': return '100%'
      default: return '0%'
    }
  }

  const modeLabels = { password: 'Password', passphrase: 'Passphrase', username: 'Username' }

  return (
    <ToolPageTemplate path="/password/" tools={tools}>
      <div>
        <div className="pw-mode-tabs" role="tablist" aria-label="Generator mode">
          {['password', 'passphrase', 'username'].map(mode => (
            <button
              key={mode}
              role="tab"
              aria-selected={generatorMode === mode}
              className={`pw-mode-tab ${generatorMode === mode ? 'active' : ''}`}
              onClick={() => dispatch(setGeneratorMode(mode))}
            >
              {modeLabels[mode]}
            </button>
          ))}
        </div>

        <section className="password-generator">
          {generatorMode === 'password' && (
            <>
              <div className="password-controls">
                <div className="control-section">
                  <RangeSlider
                    label="Password Length"
                    value={length}
                    onChange={(e) => dispatch(setLength(parseInt(e.target.value)))}
                    min={4}
                    max={128}
                    step={1}
                  />
                </div>

                <div className="control-section">
                  <h4>Character Types</h4>
                  <div className="toggle-group">
                    <div className="toggle-item">
                      <Toggle checked={includeUppercase} onChange={(e) => dispatch(setIncludeUppercase(e.target.checked))} />
                      <label>Uppercase (A-Z)</label>
                    </div>
                    <div className="toggle-item">
                      <Toggle checked={includeLowercase} onChange={(e) => dispatch(setIncludeLowercase(e.target.checked))} />
                      <label>Lowercase (a-z)</label>
                    </div>
                    <div className="toggle-item">
                      <Toggle checked={includeNumbers} onChange={(e) => dispatch(setIncludeNumbers(e.target.checked))} />
                      <label>Numbers (0-9)</label>
                    </div>
                    <div className="toggle-item">
                      <Toggle checked={includeSymbols} onChange={(e) => dispatch(setIncludeSymbols(e.target.checked))} />
                      <label>Symbols (!@#$%^&*)</label>
                    </div>
                    <div className="toggle-item">
                      <Toggle checked={excludeAmbiguous} onChange={(e) => dispatch(setExcludeAmbiguous(e.target.checked))} />
                      <label>Exclude Ambiguous (0, O, 1, l, I)</label>
                    </div>
                  </div>
                </div>

                <div className="control-section">
                  <h4>Custom Requirements</h4>
                  <p className="control-section-hint">Guarantee a minimum count of each type - useful when a site enforces its own password rules.</p>
                  <div className="toggle-group">
                    <RangeSlider
                      label="Minimum Numbers"
                      value={minNumbers}
                      onChange={(e) => dispatch(setMinNumbers(parseInt(e.target.value)))}
                      min={0}
                      max={10}
                      step={1}
                      disabled={!includeNumbers}
                    />
                    <RangeSlider
                      label="Minimum Symbols"
                      value={minSymbols}
                      onChange={(e) => dispatch(setMinSymbols(parseInt(e.target.value)))}
                      min={0}
                      max={10}
                      step={1}
                      disabled={!includeSymbols}
                    />
                  </div>
                </div>

                <div className="control-section">
                  <h4>Embed a Word or Name (Optional)</h4>
                  <Input
                    type="text"
                    label="Word to embed"
                    value={baseWord}
                    onChange={(e) => dispatch(setBaseWord(e.target.value))}
                    placeholder="e.g. a project name you need to recognize at a glance"
                  />
                  {baseWord && (
                    <div className="pw-warning-banner">
                      ⚠️ Embedding a real word or name lowers entropy - an attacker's dictionary tries recognizable words first.
                      Only use this for low-risk, throwaway accounts, never for email, banking, or your password manager.
                    </div>
                  )}
                </div>

                <div className="control-section">
                  <div className="password-buttons">
                    <Button onClick={handleGenerate}>Generate Password</Button>
                    <RangeSlider
                      label="Bulk count"
                      value={bulkCount}
                      onChange={(e) => dispatch(setBulkCount(parseInt(e.target.value)))}
                      min={2}
                      max={20}
                      step={1}
                    />
                    <Button variant="secondary" onClick={handleGenerateMultiple}>Generate {bulkCount} Passwords</Button>
                    <Button variant="ghost" onClick={() => dispatch(clearPasswords())}>Clear List</Button>
                  </div>
                </div>
              </div>

              {password && (
                <ResultBox title="Generated Password">
                  <div className="password-result">
                    <div className="password-display">
                      <span className="password-text">{password}</span>
                      <CopyButton text={password} />
                    </div>
                    <div className="password-strength">
                      <div className="strength-label">
                        <span>Password Strength:</span>
                        <span className="strength-text" style={{ color: getStrengthColor(strength) }}>
                          {getStrengthText(strength)}
                        </span>
                      </div>
                      <div className="strength-bar">
                        <div className="strength-fill" style={{ width: getStrengthWidth(strength), backgroundColor: getStrengthColor(strength) }} />
                      </div>
                    </div>
                    <div className="pw-crack-time">
                      <span>Estimated time to crack: <strong>{crackTime}</strong></span>
                      <span className="pw-entropy-note">{entropyBits} bits of entropy · assumes 10 billion guesses/sec offline attack</span>
                    </div>
                  </div>
                </ResultBox>
              )}

              {passwords.length > 0 && (
                <ResultBox title="Generated Passwords">
                  <div className="password-list">
                    {passwords.map((pwd, index) => (
                      <div key={index} className="password-item">
                        <span className="password-text">{pwd}</span>
                        <CopyButton text={pwd} />
                      </div>
                    ))}
                  </div>
                </ResultBox>
              )}
            </>
          )}

          {generatorMode === 'passphrase' && (
            <>
              <div className="password-controls">
                <div className="control-section">
                  <RangeSlider
                    label="Number of Words"
                    value={passphraseWordCount}
                    onChange={(e) => dispatch(setPassphraseWordCount(parseInt(e.target.value)))}
                    min={3}
                    max={10}
                    step={1}
                  />
                </div>
                <div className="control-section">
                  <div className="toggle-group">
                    <SelectDropdown
                      label="Word Separator"
                      value={passphraseSeparator}
                      onChange={(e) => dispatch(setPassphraseSeparator(e.target.value))}
                      options={SEPARATOR_OPTIONS}
                    />
                    <div className="toggle-item">
                      <Toggle checked={passphraseCapitalize} onChange={(e) => dispatch(setPassphraseCapitalize(e.target.checked))} />
                      <label>Capitalize Each Word</label>
                    </div>
                    <div className="toggle-item">
                      <Toggle checked={passphraseIncludeNumber} onChange={(e) => dispatch(setPassphraseIncludeNumber(e.target.checked))} />
                      <label>Append a Random Number</label>
                    </div>
                  </div>
                </div>
                <div className="control-section">
                  <div className="password-buttons">
                    <Button onClick={() => dispatch(generatePassphrase())}>Generate Passphrase</Button>
                  </div>
                </div>
              </div>

              {passphrase && (
                <ResultBox title="Generated Passphrase">
                  <div className="password-result">
                    <div className="password-display">
                      <span className="password-text">{passphrase}</span>
                      <CopyButton text={passphrase} />
                    </div>
                    <div className="pw-crack-time">
                      <span>Estimated time to crack: <strong>{passphraseCrackTime}</strong></span>
                      <span className="pw-entropy-note">{passphraseEntropyBits} bits of entropy · drawn from a 645-word list</span>
                    </div>
                  </div>
                </ResultBox>
              )}
            </>
          )}

          {generatorMode === 'username' && (
            <>
              <div className="password-controls">
                <div className="control-section">
                  <SelectDropdown
                    label="Username Style"
                    value={usernameStyle}
                    onChange={(e) => dispatch(setUsernameStyle(e.target.value))}
                    options={USERNAME_STYLE_OPTIONS}
                  />
                </div>
                {usernameStyle === 'plus-address' && (
                  <div className="control-section">
                    <Input
                      type="email"
                      label="Your email address"
                      value={baseEmail}
                      onChange={(e) => dispatch(setBaseEmail(e.target.value))}
                      placeholder="you@example.com"
                    />
                    <p className="control-section-hint">We add a random tag before the @ - e.g. you+8kd3f1@example.com - so every signup gets a unique, filterable address. Nothing is sent anywhere; this happens in your browser.</p>
                  </div>
                )}
                <div className="control-section">
                  <div className="password-buttons">
                    <Button onClick={() => dispatch(generateUsername())}>Generate Username</Button>
                    {usernames.length > 0 && (
                      <Button variant="ghost" onClick={() => dispatch(clearUsernames())}>Clear List</Button>
                    )}
                  </div>
                </div>
              </div>

              {username && (
                <ResultBox title="Generated Username">
                  <div className="password-display">
                    <span className="password-text">{username}</span>
                    <CopyButton text={username} />
                  </div>
                  {usernames.length > 1 && (
                    <div className="pw-username-chips">
                      {usernames.slice(1).map((u, i) => (
                        <span key={i} className="pw-username-chip">
                          {u} <CopyButton text={u} />
                        </span>
                      ))}
                    </div>
                  )}
                </ResultBox>
              )}
            </>
          )}

          {history.length > 0 && (
            <ResultBox title="">
              <div className="pw-history-header">
                <h3>Recently Generated (this session)</h3>
                <button className="pw-clear-history-btn" onClick={() => dispatch(clearHistory())}>Clear History</button>
              </div>
              <div className="pw-history">
                {history.map(item => (
                  <div key={item.id} className="pw-history-item">
                    <span className="pw-history-badge">{item.type}</span>
                    <span className="pw-history-value">{item.value}</span>
                    <CopyButton text={item.value} />
                  </div>
                ))}
              </div>
            </ResultBox>
          )}
        </section>

        <section className="tool-guide">
          <div className="guide-section">
            <h2>What Is a Password Generator?</h2>
            <p>A password generator is a tool that creates long, random passwords using a cryptographically secure random number generator (CSPRNG), so the result has no pattern an attacker can predict. This one runs entirely in your browser — nothing you generate is sent to a server, logged, or stored.</p>
            <p>The reason random beats "clever" is math. Security is measured in bits of entropy: every extra character multiplies the number of possible combinations. A 16-character password drawn from the full 94-character keyboard set has roughly 10<sup>31</sup> possible values — about 105 bits of entropy. Even an attacker guessing billions of times per second against a stolen database would need longer than the age of the universe to work through that space.</p>
            <p>Human-chosen passwords fail because they cluster around predictable patterns — a capital at the start, a year at the end, a "!" for the required symbol. Attackers know these patterns and test them first. A generator removes the human bias entirely, which is why every serious security guideline recommends generated passwords paired with a password manager.</p>
          </div>

          {/* NEW: Why this generator vs others */}
          <div className="guide-section">
            <h2>Why Use This Generator Instead of One Built Into a Password Manager?</h2>
            <p>Password managers like 1Password, Bitwarden, and LastPass all include built-in generators. So why use a standalone one?</p>
            <p><strong>Privacy and trust.</strong> A standalone tool that runs entirely in your browser and doesn't have your account credentials is a different trust model than a generator inside a service that stores all your passwords. If you don't want to log into a password manager just to create a throwaway account password, a browser-based generator avoids that.</p>
            <p><strong>Length and character control.</strong> Most manager generators cap at 20–30 characters and don't expose the fine-grained controls you'll find here: minimum symbol counts, ambiguous character exclusion, embedding a specific word, or generating passphrases from a curated word list. When a sign-up form has unusual requirements, this tool adapts.</p>
            <p><strong>No install required.</strong> On a shared or temporary device where you don't want to install a password manager, this tool works from any browser in seconds. Generate, copy, use — nothing persists.</p>
            <p>Where password managers are still the right choice: for actually <em>storing</em> the passwords you generate. A generator creates a strong password; a manager remembers it. They're complementary, not alternatives. Pair this tool with a manager for the ideal setup.</p>
          </div>

          <div className="guide-section">
            <h2>Three Ways to Generate a Credential</h2>
            <p>This tool covers the three credentials most sign-up forms actually ask for, switchable from the tabs above the generator:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>Password</h3>
                <p>Maximum-entropy random characters. Best whenever a password manager will store and autofill the result for you.</p>
              </div>
              <div className="use-case">
                <h3>Passphrase</h3>
                <p>Several random dictionary words strung together. Nearly as strong as a random password, but realistic to type from memory.</p>
              </div>
              <div className="use-case">
                <h3>Username</h3>
                <p>A random word-based handle, or a unique "plus-addressed" email variant for tracking exactly who leaks or sells your address.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>What Modern Security Guidance Actually Says (2026)</h2>
            <p>A lot of "password advice" online is out of date. Current guidance from the U.S. National Institute of Standards and Technology (NIST SP 800-63B) — the standard most security teams follow — has changed the rules in ways worth knowing:</p>
            <p><strong>Length matters more than complexity.</strong> A long password is stronger than a short one stuffed with symbols. NIST recommends supporting passwords up to at least 64 characters and treats length as the primary strength factor.</p>
            <p><strong>Forced periodic changes are discouraged.</strong> The old "change your password every 90 days" rule is no longer recommended, because it pushes people toward weak, incremental passwords (Summer2024 → Summer2025). You should change a password when there is a reason to — a breach, a shared device, a phishing scare — not on a calendar.</p>
            <p><strong>Uniqueness is non-negotiable.</strong> The biggest real-world risk today is credential stuffing: attackers take passwords leaked from one breached site and try them everywhere else. A unique generated password per account makes that attack impossible, even if one site is compromised.</p>
          </div>

          <div className="guide-section">
            <h2>Passphrase vs. Random Password: Which Should You Use?</h2>
            <p><strong>Short answer:</strong> use a random password when a password manager will remember it for you, and a passphrase when you genuinely have to type or recall it yourself. Both can reach comparable security — the difference is memorability, not strength.</p>
            <p>NIST SP 800-63B explicitly endorses passphrases as a valid, user-friendly way to hit a long minimum length without forcing people into unmemorable symbol soup. A random password packs the most entropy into the fewest characters, which is ideal when you'll never actually type it. A passphrase trades a few extra characters for something a human brain can hold onto — useful for a device PIN screen, a shared home Wi-Fi password, or your password manager's own master password, which is the one password you truly cannot store anywhere else.</p>
          </div>

          <div className="guide-section">
            <h2>How Secure Is This Passphrase Generator?</h2>
            <p>Each word in a generated passphrase is drawn independently from a curated list of <strong>645 everyday English words</strong> — common nouns, adjectives, and verbs, filtered to exclude anything ambiguous, offensive, or awkward to spell. That gives each word position roughly <strong>log₂(645) ≈ 9.3 bits</strong> of entropy.</p>
            <p>A default 5-word passphrase (with the optional trailing number enabled) therefore carries roughly <strong>50 bits of entropy</strong> — comparable to an 8-character fully-random password, but built from words a person can actually remember and type without looking. Add a sixth or seventh word using the slider above if you want to push a passphrase toward "very strong" territory for a more critical account.</p>
            <p>This is the same principle behind well-known passphrase examples like "correct horse battery staple": length in words, not visual complexity, is what drives real security — as long as the words themselves are chosen by a random process, not by you.</p>
          </div>

          <div className="guide-section">
            <h2>How This Generator Works</h2>
            <p>When you click generate, the tool builds a character pool from the options you enable — 26 lowercase letters, 26 uppercase, 10 digits, and a set of symbols — then uses your browser's built-in <code>crypto.getRandomValues()</code> CSPRNG to pick each character. That function is the same class of randomness used for encryption keys, not the ordinary <code>Math.random()</code> that can be predicted.</p>
            <p>Every position is chosen with uniform probability, so no character or pattern is favored. If you set a custom requirement — say, "at least 3 numbers" — the generator checks the result afterward and swaps in extra digits only if the random draw happened to fall short, so your requirement is always met without weakening the rest of the password. The strength meter then estimates entropy from your length and character choices and maps it to both a categorical rating and the time-to-crack estimate explained below.</p>
            <p>Because all of this happens locally in JavaScript, your password never leaves the page. Closing the tab is enough to erase it — there is nothing on our side to leak. The same applies to the session history panel above: it lives only in your browser's memory for this visit and disappears the moment you refresh or navigate away.</p>
          </div>

          <div className="guide-section">
            <h2>Understanding Your Password's Time-to-Crack Estimate</h2>
            <p><strong>What it means:</strong> the "estimated time to crack" figure shown with every generated password is how long an offline attacker would take, on average, to guess it through brute force — assuming they already have a stolen copy of the password's hash and are testing candidates as fast as modern hardware allows.</p>
            <p><strong>Our methodology, stated plainly:</strong> we assume 10 billion guesses per second, a commonly cited benchmark for a modern GPU rig attacking a fast, unsalted hash, and we estimate the average case as half of the full keyspace (an attacker expects to find a match partway through an exhaustive search, not right at the end). This is a deliberately conservative, worst-case-for-you estimate — a properly salted, slow hash like bcrypt or Argon2 would take dramatically longer to crack than the number shown here. We'd rather under-promise on security than overstate it.</p>
            <p>The practical takeaway holds regardless of the exact assumptions: each additional character multiplies the keyspace rather than just adding to it, which is why going from 8 to 16 characters doesn't double your security — it raises it by many orders of magnitude.</p>
          </div>

          <div className="guide-section">
            <h2>Custom Password Requirements: Minimum Numbers & Symbols</h2>
            <p><strong>What this is for:</strong> some sign-up forms enforce their own rules — "must contain at least 2 numbers," "at least 1 special character." The Minimum Numbers and Minimum Symbols sliders let you guarantee those counts up front, so you're never stuck regenerating a password over and over hoping it happens to qualify.</p>
            <p>The generator always keeps your requested minimums without shrinking the rest of the password's randomness — if a request would be mathematically impossible for the length you've chosen (for example, asking for more numbers and symbols combined than there are characters), it automatically gives numbers priority and fits as many symbols as the remaining space allows, rather than silently failing.</p>
          </div>

          <div className="guide-section">
            <h2>Should You Put Your Name in a Password?</h2>
            <p><strong>Short answer: generally, no.</strong> Personal information — names, birthdates, pet names — is the very first thing an attacker's cracking dictionary tries, which is why our own <Link to="/blog/complete-guide-to-strong-password-security/">password security guide</Link> lists it as a top mistake to avoid.</p>
            <p>That said, there are legitimate, low-risk situations where embedding a recognizable word helps — labeling a shared team login with a project codename, or creating something you can recognize at a glance across a spreadsheet of test accounts. The "Embed a Word or Name" field above does exactly this: it drops your word into an otherwise fully random password at a random position. We show a visible warning whenever it's used, and the strength meter is deliberately marked down to reflect the real entropy loss — we'd rather be honest about the trade-off than let the feature look safer than it is. Never use it for email, banking, or your password manager's master password.</p>
          </div>

          <div className="guide-section">
            <h2>Free Username Generator</h2>
            <p><strong>What it does:</strong> the Username tab creates either a random word-plus-number handle (for forums, gaming, or any account where you don't want to reuse the same username everywhere), or a "plus-addressed" version of your own email — appending a random tag before the @ symbol, like <code>you+8kd3f1@example.com</code>. Most major email providers (Gmail, Outlook, and others) deliver plus-addressed mail straight to your normal inbox while letting you filter or block by tag.</p>
            <p>The practical use case for plus-addressing: give every service a slightly different address, and if you ever start receiving spam on one specific tag, you know exactly which company leaked or sold it — without needing a new inbox.</p>
          </div>

          {/* NEW: When to change password */}
          <div className="guide-section">
            <h2>When Should You Change a Password?</h2>
            <p>Modern security guidance (NIST SP 800-63B) specifically discourages forced periodic changes — the "change every 90 days" rule creates more problems than it solves because it pushes users toward incremental variations that are easy to guess. Change a password when one of these specific events occurs:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>After a data breach</h3>
                <p>If a service you use announces a breach, change that account's password immediately. Also change it on any other service where you reused the same password — which is one of the main reasons to never reuse them in the first place.</p>
              </div>
              <div class="use-case">
                <h3>After a phishing scare</h3>
                <p>If you clicked a link in a suspicious email, entered credentials, or have any doubt about whether a login was legitimate, change the password immediately as a precaution.</p>
              </div>
              <div className="use-case">
                <h3>After a device is lost or shared</h3>
                <p>If you logged into an account on a device that's now lost, sold, or used by someone you no longer trust, change that account's password from a device you control.</p>
              </div>
              <div className="use-case">
                <h3>After sharing a password</h3>
                <p>If you ever gave a password to someone — a colleague, family member, support agent — change it as soon as the shared use is over. Shared passwords are the most common source of account compromise.</p>
              </div>
              <div className="use-case">
                <h3>After a period of inactivity on a critical account</h3>
                <p>For high-value accounts (email, banking, cloud storage), a change once a year is a reasonable precaution — not because the password is likely compromised, but because the account is valuable enough to justify the cost of changing it.</p>
              </div>
            </div>
            <p>Outside of these events, there's no security benefit to changing a strong, unique password. Save the effort for the accounts and moments that actually matter.</p>
          </div>

          <div className="guide-section">
            <h2>How to Use This Password Generator</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Pick a mode</h3>
                  <p>Choose Password for maximum entropy, Passphrase for something memorable, or Username for a handle or plus-addressed email — using the tabs at the top.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Set your length or word count</h3>
                  <p>Use 16+ characters (or 6+ words for a passphrase) for important accounts, and at least 12 characters (or 4+ words) for everything else.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Add any custom requirements</h3>
                  <p>If a site demands specific rules, set your minimum numbers and symbols so the result is guaranteed to qualify on the first try.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Generate, check the crack-time, and copy</h3>
                  <p>Review the time-to-crack estimate for a sense of real-world strength, then copy the result straight into the signup form and save it in a password manager.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>How Long Should Your Password Be?</h2>
            <div className="use-cases">
              <div className="use-case">
                <h3>High-value accounts</h3>
                <p><strong>16–24 characters, all types.</strong> Email, banking, cloud storage, and your password manager's master password. These are the accounts that can reset every other login, so give them the most entropy.</p>
              </div>
              <div className="use-case">
                <h3>Everyday logins</h3>
                <p><strong>14–16 characters, mixed types.</strong> Social media, work SaaS tools, and shopping accounts. Strong and effortless when a password manager stores and fills them.</p>
              </div>
              <div className="use-case">
                <h3>Low-risk or throwaway accounts</h3>
                <p><strong>12 characters minimum.</strong> Forums, newsletters, one-off signups. Still generate a unique one — reused "junk" passwords are exactly what credential-stuffing attacks feed on.</p>
              </div>
              <div className="use-case">
                <h3>Passwords you must type manually</h3>
                <p><strong>16–18 characters, ambiguous excluded — or switch to Passphrase mode.</strong> Wi-Fi keys, smart-TV and game-console logins. An 18-character random password or a 5-word passphrase both work well here; pick whichever you'll actually type correctly on the first try.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>Is it safe to use an online password generator?</h3>
                <p>It's safe when generation happens locally in your browser, as it does here. This tool uses your device's built-in cryptographic randomness and never transmits or stores what it creates — the password exists only on your screen until you close the tab. Avoid any generator that shows your password on a server-side page or asks you to log in first.</p>
              </div>
              <div className="faq-item">
                <h3>What is a passphrase generator and how is it different from a regular password generator?</h3>
                <p>A passphrase generator strings together several random dictionary words (like "Otter-Canyon-Ember-Trumpet-42") instead of random individual characters. It's a different route to the same goal — high entropy — optimized for something a human can actually memorize and type, rather than the absolute maximum entropy per character that a random password achieves.</p>
              </div>
              <div className="faq-item">
                <h3>How many words should my passphrase have?</h3>
                <p>Five words is a solid everyday default, giving roughly 50 bits of entropy from our 645-word list. Use 4 words for lower-risk accounts, or 6–7 words for anything you'd treat as a high-value account, similar to how you'd size a random password's length.</p>
              </div>
              <div className="faq-item">
                <h3>Can I set a minimum number of digits or symbols in my password?</h3>
                <p>Yes — the Minimum Numbers and Minimum Symbols sliders in Password mode guarantee those counts appear in the result, which is useful when a specific site enforces its own character-type rules. The rest of the password stays fully random.</p>
              </div>
              <div className="faq-item">
                <h3>Is it safe to generate a password using my name?</h3>
                <p>Not for anything important. Names and other personal details are the first thing password-cracking dictionaries try. The optional "embed a word or name" field exists for low-risk, throwaway use cases where recognizability matters more than maximum security — we deliberately lower the displayed strength rating whenever it's used so you always see the honest trade-off.</p>
              </div>
              <div className="faq-item">
                <h3>How is the time-to-crack estimate calculated?</h3>
                <p>We calculate the password's entropy in bits from its length and character set, then estimate an offline attacker guessing at 10 billion attempts per second — a commonly cited rate for modern GPU hardware against a fast, unsalted hash — reaching the average case at roughly half the full keyspace. It's a conservative, worst-case estimate; a properly salted, slow hash (like bcrypt or Argon2) would take considerably longer in practice.</p>
              </div>
              <div className="faq-item">
                <h3>What is a good password length? Should I use 18 characters?</h3>
                <p>18 characters is a strong, comfortable choice — long enough to push almost any character combination well past "very strong," and short enough to type manually if a device doesn't support paste. Our general guidance: 12 characters as an absolute floor, 16+ for anything important, and 18–20+ for passwords you may occasionally need to type by hand, like a Wi-Fi key.</p>
              </div>
              <div className="faq-item">
                <h3>Does this tool generate usernames too?</h3>
                <p>Yes — switch to the Username tab for either a random word-and-number handle, or a plus-addressed variant of your own email (you+randomtag@yourdomain.com) that most inboxes deliver normally while letting you track or filter by tag.</p>
              </div>
              <div className="faq-item">
                <h3>Can I generate more than 5 passwords at once?</h3>
                <p>Yes — use the bulk-count slider next to the "Generate Multiple" button to choose anywhere from 2 to 20 passwords in one click, useful for setting up several accounts at once.</p>
              </div>
              <div className="faq-item">
                <h3>How long does it take to crack a strong password?</h3>
                <p>For a 16-character random password using all character types, an attacker guessing offline at billions of attempts per second would still need far longer than the age of the universe. Short passwords are the danger: an 8-character password with no complexity can fall in hours. Length is what pushes crack time from "seconds" to "effectively forever."</p>
              </div>
              <div className="faq-item">
                <h3>Do I really need to change my passwords every few months?</h3>
                <p>No. Current NIST guidance advises against forced periodic changes, because they tend to produce weaker, predictable variations. Change a password when there's a real reason — a data breach, a shared or lost device, or any sign of compromise — not on a fixed schedule. A unique password per site plus two-factor authentication matters far more.</p>
              </div>
              <div className="faq-item">
                <h3>Why does the tool let me exclude symbols or ambiguous characters?</h3>
                <p>Some systems reject certain symbols, and some passwords you have to read or type by hand. Excluding symbols keeps a password compatible with strict sites, while excluding ambiguous characters (0/O, 1/l/I) prevents misreads. When you trim the pool, just add a few characters of length to keep the same overall strength.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/qr-generator/" className="related-tool">
                <div className="tool-icon">QR Generator</div>
                <div className="tool-description">Turn a Wi-Fi password into a scannable QR code for guests</div>
              </Link>
              <Link to="/word-counter/" className="related-tool">
                <div className="tool-icon">Word Counter</div>
                <div className="tool-description">Check the length and character count of any text</div>
              </Link>
              <Link to="/json-formatter/" className="related-tool">
                <div className="tool-icon">JSON Formatter</div>
                <div className="tool-description">Format and validate config files and API responses</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default PasswordPage